/**
 * Where Nob reaches YOU (`/you`) — the address the product writes to.
 *
 * It is not the address you signed in with, and the difference is the whole
 * reason this group exists: a door is addressed at an X handle, and an X
 * identity may never have handed over a mailbox at all.
 *
 * CHANGING IT PROVES IT, exactly as naming it did (ruling R-0826-3). Under the
 * old shape this form saved whatever was typed, unverified, and a typo here
 * meant every later message — receipts, the trial reminder, the dunning ladder,
 * the Morning Nob — delivered nowhere with nobody told. So this is the SAME
 * machinery the onboarding Email step runs: a code goes to the new address, and
 * `users.contact_email` does not move until it comes back. The address on
 * screen therefore stays the working one for as long as the change is
 * unfinished, which is the point — a half-finished change must not cost
 * somebody the mailbox they already had.
 *
 * There is no way to UNSET it here, deliberately. Set and change are the only
 * honest verbs for something the product needs in order to deliver at all; an
 * "unset" would be a button whose consequence is a briefing nobody receives.
 *
 * The server owns the rules about what an address is. This form checks the one
 * thing it can check without guessing — that something was typed — and prints
 * the API's refusal verbatim beside the field that earned it.
 */
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { confirmEmailCode, requestEmailCode, type Me } from "../../lib/api";
import { noteCodeSentTo } from "../../lib/dev";
import { qk } from "../../lib/queries";
import { LinkButton, SecondaryButton, QuietButton } from "../ui/button";
import { Group, PLATE } from "../shell/surfaces";

export const REACH_TITLE = "Where Nob reaches you";
export const REACH_LEAD =
  "Your Morning Nob lands here, and so does anything else Nob has to put in writing.";
export const NOWHERE_TO_DELIVER = "Morning Nob has nowhere to deliver until this is set.";
/** Why an owner can be here with nothing set at all, said once, beside the gap. */
export const NO_ADDRESS_FROM_X =
  "A door is addressed at an X handle, and X hands over no mailbox — so Nob has never been told one.";
/** What the server refused with, when it refused without saying anything. */
export const CONTACT_UNREADABLE = "Nob could not read that address.";
export const NOT_SET = "Not set.";
export const ADD_LABEL = "Add an email";
export const CHANGE_LABEL = "Change";
export const CONTACT_SEND_LABEL = "Send the code";
export const CONTACT_CONFIRM_LABEL = "Confirm";
export const CONTACT_CANCEL_LABEL = "Not now";
export const CONTACT_SAVED_ACK = "Saved.";
export const CONTACT_FIELD_LABEL = "Email address";
export const CONTACT_CODE_LABEL = "6-digit code";
export const CONTACT_UNREACHABLE = "Nob is not answering. Try that again.";
export const CONTACT_MAIL_UNAVAILABLE = "Nob could not get that code into the post. Try again.";
export const CONTACT_EXPIRED = "That code has expired. Ask for another.";
export const CONTACT_LOCKED_OUT = "Too many wrong tries. Ask for a new code.";
export const CONTACT_NO_PENDING = "There is no code to check. Ask for one.";

/** Where the code went and how long it lives, in the server's masked form. */
export function contactSentLine(masked: string): string {
  return `Six digits, on their way to ${masked}. Nothing changes until they come back.`;
}
export function contactWrongCode(left: number): string {
  return left === 1
    ? "That code is not right. One try left, then Nob sends a new one."
    : `That code is not right. ${left} tries left.`;
}

/** How long the acknowledgement stays on screen. Long enough to read, once. */
const ACK_MS = 2000;

const FIELD =
  "h-9 w-full max-w-xs rounded-lg border border-border bg-bg/60 px-3 text-sm text-text " +
  "placeholder:text-fainter focus-visible:border-border-loud focus-visible:outline-none " +
  "focus-visible:ring-2 focus-visible:ring-focus/40 disabled:opacity-50 " +
  "aria-[invalid=true]:border-danger/60";

/**
 * The sign-in address, when this session has one and it is a DIFFERENT string
 * from the delivery address. Said only then: repeating one address twice under
 * two labels is how a screen teaches somebody they have two mailboxes.
 */

type Phase = "closed" | "address" | "code";

export function ContactEmailSection({ me }: { me: Me }) {
  const queryClient = useQueryClient();
  const headingId = useId();
  const current = me.contact_email;

  const [phase, setPhase] = useState<Phase>("closed");
  const [value, setValue] = useState(current ?? "");
  const [code, setCode] = useState("");
  const [masked, setMasked] = useState("");
  const [busy, setBusy] = useState(false);
  const [issue, setIssue] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const field = useRef<HTMLInputElement>(null);
  const codeField = useRef<HTMLInputElement>(null);
  const ack = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(ack.current), []);

  const open = useCallback(() => {
    setIssue(null);
    setValue(current ?? "");
    setCode("");
    setPhase("address");
  }, [current]);

  // The caret goes where the typing goes; a form that opens under the pointer
  // and waits to be clicked is a form that opened for nobody.
  useEffect(() => {
    if (phase === "address") field.current?.focus();
    if (phase === "code") codeField.current?.focus();
  }, [phase]);

  const send = useCallback(() => {
    const email = value.trim();
    if (!email || busy) return;
    setBusy(true);
    setIssue(null);
    void requestEmailCode(email).then((r) => {
      setBusy(false);
      if (!r.ok) {
        if (r.code === "TOO_SOON") {
          // A code went out recently and is still live. Move to the field that
          // spends it rather than refusing a thing that has in fact happened.
          setMasked("");
          setPhase("code");
          return;
        }
        // A refusal keeps the form open with the words in it. The server's own
        // sentence when it wrote one; ours only when it did not.
        setIssue(
          r.message ??
            (r.code === "VALIDATION_ERROR"
              ? CONTACT_UNREADABLE
              : r.code === "MAIL_UNAVAILABLE"
                ? CONTACT_MAIL_UNAVAILABLE
                : CONTACT_UNREACHABLE),
        );
        return;
      }
      setMasked(r.to);
      setCode("");
      setPhase("code");
      // The local flow-testing strip reads the code back from here. Inert
      // everywhere the kit is not mounted.
      noteCodeSentTo(email);
    });
  }, [busy, value]);

  const confirm = useCallback(() => {
    const typed = code.trim();
    if (!typed || busy) return;
    setBusy(true);
    setIssue(null);
    void confirmEmailCode(typed).then((r) => {
      setBusy(false);
      if (!r.ok) {
        if (r.code === "WRONG_CODE") {
          setIssue(contactWrongCode(r.attempts_left));
          setCode("");
          return;
        }
        // Expired, locked out or nothing pending all mean "ask for another",
        // which is the only thing this panel can do about any of them.
        setIssue(
          r.code === "EXPIRED"
            ? CONTACT_EXPIRED
            : r.code === "LOCKED_OUT"
              ? CONTACT_LOCKED_OUT
              : r.code === "NO_PENDING"
                ? CONTACT_NO_PENDING
                : CONTACT_UNREACHABLE,
        );
        setCode("");
        setPhase("address");
        return;
      }
      setPhase("closed");
      noteCodeSentTo(null);
      // The server's copy is what everything else on screen reads — including
      // the run state, which this write can move out of `email_pending`.
      void queryClient.invalidateQueries({ queryKey: qk.me() });
      void queryClient.invalidateQueries({ queryKey: qk.onboarding() });
      setSaved(true);
      clearTimeout(ack.current);
      ack.current = setTimeout(() => setSaved(false), ACK_MS);
    });
  }, [busy, code, queryClient]);

  return (
    // Named for anyone not reading it visually — and because the Account page
    // has more than one Save on it, which is a thing a screen reader has to be
    // able to tell apart as surely as a test does. The section is the Group's
    // own <section>; the title is the Group's label, which is the same words in
    // the same role as the <h2> that stood here.
    <Group title={REACH_TITLE} titleId={headingId}>
      <div className={PLATE}>
        <p className="text-[12px] leading-snug text-faint">{REACH_LEAD}</p>

        {phase === "address" && (
          <form
            className="mt-3"
            // The SERVER owns what an address is, and this form prints its
            // sentence. Native constraint validation would swallow the submit and
            // answer with a browser tooltip instead — a refusal in somebody
            // else's words, at a moment Nob never heard about.
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
          >
            <label
              className="block text-[11px] uppercase tracking-[0.12em] text-faint"
              htmlFor="contact-email"
            >
              {CONTACT_FIELD_LABEL}
            </label>
            <input
              id="contact-email"
              ref={field}
              type="email"
              autoComplete="email"
              placeholder="you@company.com"
              value={value}
              disabled={busy}
              aria-invalid={issue !== null}
              {...(issue ? { "aria-describedby": "contact-email-error" } : {})}
              onChange={(e) => setValue(e.target.value)}
              className={`${FIELD} mt-1.5`}
            />
            {issue && (
              <p id="contact-email-error" role="alert" className="mt-1.5 text-[13px] text-danger">
                {issue}
              </p>
            )}
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <SecondaryButton type="submit" disabled={busy || value.trim().length === 0}>
                {CONTACT_SEND_LABEL}
              </SecondaryButton>
              <QuietButton type="button" onClick={() => setPhase("closed")} disabled={busy}>
                {CONTACT_CANCEL_LABEL}
              </QuietButton>
            </div>
          </form>
        )}

        {phase === "code" && (
          <form
            className="mt-3"
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              confirm();
            }}
          >
            {masked && (
              <p className="text-[12px] leading-snug text-text-soft">{contactSentLine(masked)}</p>
            )}
            <label
              className="mt-3 block text-[11px] uppercase tracking-[0.12em] text-faint"
              htmlFor="contact-email-code"
            >
              {CONTACT_CODE_LABEL}
            </label>
            <input
              id="contact-email-code"
              ref={codeField}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="000000"
              value={code}
              disabled={busy}
              aria-invalid={issue !== null}
              {...(issue ? { "aria-describedby": "contact-email-error" } : {})}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              className={`${FIELD} mt-1.5 font-mono tracking-[0.4em]`}
            />
            {issue && (
              <p id="contact-email-error" role="alert" className="mt-1.5 text-[13px] text-danger">
                {issue}
              </p>
            )}
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <SecondaryButton type="submit" disabled={busy || code.trim().length === 0}>
                {CONTACT_CONFIRM_LABEL}
              </SecondaryButton>
              <QuietButton type="button" onClick={() => setPhase("closed")} disabled={busy}>
                {CONTACT_CANCEL_LABEL}
              </QuietButton>
            </div>
          </form>
        )}

        {phase === "closed" && (
          <>
            <div className="mt-3 flex flex-wrap items-baseline gap-x-4 gap-y-2">
              {current ? (
                <p className="truncate font-mono text-[13px] text-text-soft">{current}</p>
              ) : (
                <p className="text-[13px] text-faint">{NOT_SET}</p>
              )}
              <LinkButton type="button" onClick={open}>
                {current ? CHANGE_LABEL : ADD_LABEL}
              </LinkButton>
              {saved && (
                <span className="font-mono text-[11px] text-accent-ink/80" role="status">
                  {CONTACT_SAVED_ACK}
                </span>
              )}
            </div>

            {/* The one consequence of leaving it blank, stated where the blank is
                — not in a mailbox nobody is watching. Rare now that onboarding
                collects it, and kept because a row can still be reached that way
                (an account made before the step, or a change abandoned). */}
            {!current && (
              <div className="mt-3 border-l-2 border-accent/40 pl-3">
                <p className="text-[12px] leading-snug text-accent-ink-strong/80">
                  {NOWHERE_TO_DELIVER}
                </p>
                {/* An owner who has never given an address at all is owed the
                    reason there is nothing here to change. There is no second
                    address it could have come from (R-0828-3), so the sentence
                    is unconditional. */}
                <p className="mt-1.5 text-[12px] leading-snug text-faint">{NO_ADDRESS_FROM_X}</p>
              </div>
            )}
          </>
        )}
      </div>
    </Group>
  );
}
